/**
 * Energy-density bands: GearSkeptic's Hiker Food Chart colour key, which the Long Distance
 * Hiking Food Resupply Tool copies in its Keys tab. The bands are in kcal per ounce, as the
 * sheets state them; kcal per gram is converted in `units.ts`.
 *
 *   Heavy        below 110 kcal/oz   (below 3.88 kcal/g)
 *   Moderate     110 to 125          (3.88 to 4.41)
 *   Lightweight  125 to 140          (4.41 to 4.94)
 *   Very light   140 to 155          (4.94 to 5.47)
 *   Ultralight   155 to 170          (5.47 to 6.00)
 *   Hyperlight   above 170           (above 6.00)
 */
import { perGToPerOz, perOzToPerG } from './units'

export type DensityBand =
  | 'heavy'
  | 'moderate'
  | 'lightweight'
  | 'very-light'
  | 'ultralight'
  | 'hyperlight'

export interface DensityBandInfo {
  band: DensityBand
  label: string
  /** Lower bound, kcal per ounce (inclusive); 0 for the first band. */
  min_kcal_per_oz: number
  /** The same bound in kcal per gram. */
  min_kcal_per_g: number
}

const BOUNDS: [DensityBand, string, number][] = [
  ['heavy', 'Heavy', 0],
  ['moderate', 'Moderate', 110],
  ['lightweight', 'Lightweight', 125],
  ['very-light', 'Very light', 140],
  ['ultralight', 'Ultralight', 155],
  ['hyperlight', 'Hyperlight', 170],
]

/** Lightest band last. */
export const DENSITY_BANDS: readonly DensityBandInfo[] = BOUNDS.map(([band, label, oz]) => ({
  band,
  label,
  min_kcal_per_oz: oz,
  min_kcal_per_g: perOzToPerG(oz),
}))

/** The band for an energy density in kcal/g; undefined with no energy (water, salt, unknown). */
export function densityBand(kcalPerG: number | undefined): DensityBand | undefined {
  if (kcalPerG === undefined || !Number.isFinite(kcalPerG) || kcalPerG <= 0) return undefined
  const perOz = perGToPerOz(kcalPerG)
  let out: DensityBand = 'heavy'
  for (const b of DENSITY_BANDS) if (perOz >= b.min_kcal_per_oz) out = b.band
  return out
}

export function densityBandInfo(band: DensityBand): DensityBandInfo {
  return DENSITY_BANDS.find((b) => b.band === band) as DensityBandInfo
}

/** Rank of a band, 0 (heavy) to 5 (hyperlight), for sorting and "at least" filters. */
export function densityRank(band: DensityBand | undefined): number {
  return band ? DENSITY_BANDS.findIndex((b) => b.band === band) : -1
}
